// dashboard
import Login from "pages/Authentication/Login";
import Logout from "pages/Authentication/LogOut";
import Register from "pages/Authentication/Register";
import UserProfile from "pages/Authentication/UserProfile";
import Dashboard from "pages/Dashboards/Ecommerce";
import Users from "pages/Users";
import AppConfigurations from "pages/AppConfigurations";
import Market from "pages/Market";
import DeclareResult from "pages/DeclareResult";
import StarlineDeclareResult from "pages/StarlineDeclareResult";
import BetHistory from "pages/BetHistory";
import StarlineBetHistory from "pages/StarlineBetHistory";
import PayoutMultiplier from "pages/PayoutMultiplier";
import ManageApps from "pages/ManageApps";
import WithdrawRequest from "pages/WithdrawRequest";
import DepositRequest from "pages/DepositRequest";
import DepositAnalytics from "pages/DepositAnalytics";
import AppCustomization from "pages/AppCustomization";
import Refral from "pages/Refral";
import ReferredUsers from "pages/ReferredUsers";
import PaymentsPage from "pages/Payments";
import NotificationsPage from "pages/Notifications";
import SendNotificationPage from "pages/Notifications/SendNotificationPage";
import AddFund from "pages/AddFund";
import BankDetails from "pages/BankDetails";
import SalesReport from "pages/SalesReport";
import SalesData from "pages/SalesReport/SalesData";
import RoleCreation from "../pages/RoleCreation/RoleCreation";
import ChangePassword from "pages/ChangePassword";
import GeneralSettings from "pages/GeneralSettings";
import HFCM from "pages/HFCM";
import GameRates from "pages/GameRates";
import AgentCommissionSummary from "pages/AgentCommissionSummary";
import StarlineMarket from "pages/StarlineMarket";
import PlayersPage from "pages/Operations/Players";
import PlayerAppStatusPage from "pages/Operations/PlayerAppStatus";
import PlayerDetailsPage from "pages/Operations/PlayerDetails";
import WalletLedgerPreview from "pages/WalletLedgerPreview";

// Helper function to get role-based URL
export const getRoleBasedUrl = (path: string, role?: string | null): string => {
  if (!role) return path;
  
  const roleLower = role.toLowerCase();
  
  // Only add role prefix for specific roles that need it
  if (roleLower === 'agent (internal)' || roleLower === 'finance' || roleLower === 'support' || roleLower === 'sub admin' || roleLower === 'sub_admin') {
    const rolePrefix = roleLower.replace(' ', '').replace('(', '').replace(')', '').replace('_', '');
    return `/${rolePrefix}${path}`;
  }
  
  return path;
};

// Helper function to extract role from URL
export const extractRoleFromUrl = (pathname: string): { role: string | null; cleanPath: string } => {
  const pathSegments = pathname.split('/').filter(Boolean);
  
  if (pathSegments.length === 0) {
    return { role: null, cleanPath: '/' };
  }
  
  const firstSegment = pathSegments[0];
  
  // Check if first segment is a role
  if (['agentinternal', 'finance', 'support', 'subadmin'].includes(firstSegment)) {
    let role = firstSegment;
    if (firstSegment === 'agentinternal') {
      role = 'agent (internal)';
    } else if (firstSegment === 'subadmin') {
      role = 'sub_admin';
    }
    const cleanPath = '/' + pathSegments.slice(1).join('/');
    return { role, cleanPath: cleanPath || '/' };
  }
  
  return { role: null, cleanPath: pathname };
};


interface RouteObject {
  path: string;
  component: React.ComponentType<any>; // Use React.ComponentType to specify the type of the component
  exact?: boolean;
}

const authProtectedRoutes: Array<RouteObject> = [
  // Dashboard - Original paths
  { path: "/", component: Dashboard },
  { path: "/dashboard", component: Dashboard },
  
  // Market
  { path: "/market", component: Market },

  // Result Management
  { path: "/declare-result", component: DeclareResult },
  { path: "/starline/declare-result", component: StarlineDeclareResult },
  
  // Starline
  { path: "/starline/market", component: StarlineMarket },
  { path: "/starline/bet-history", component: StarlineBetHistory },
  
  // Bet History
  { path: "/bet-history", component: BetHistory },
  { path: "/players", component: PlayersPage },
  { path: "/player-app-status", component: PlayerAppStatusPage },
  { path: "/players/:playerId", component: PlayerDetailsPage },
  { path: "/wallet-ledger-preview", component: WalletLedgerPreview },
  { path: "/wallet-ledger-preview/:playerId", component: WalletLedgerPreview },
  
  // Payout Multiplier
  { path: "/payout-multiplier", component: PayoutMultiplier },
  
  // Manage Apps
  { path: "/manage-apps", component: ManageApps },
  
  // Withdraw History (disabled for Admin role)
  // { path: "/withdraw-history", component: WithdrawHistory },
  
  // Withdraw Request
  { path: "/withdraw-request", component: WithdrawRequest },
  
  // Deposit History
  //{ path: "/deposit-history", component: DepositHistory },
  
  // Deposit Request
  { path: "/deposit-request", component: DepositRequest },

  // Deposit Analytics
  { path: "/deposit-analytics", component: DepositAnalytics },
  
  // Add Fund (Wallet)
  { path: "/add-fund", component: AddFund },

  // Bank Details
  { path: "/bank-details", component: BankDetails },
  
  // profile
  { path: "/user-profile", component: UserProfile },
  
  // users
  { path: "/users", component: Users },

  // notifications
  { path: "/notifications", component: NotificationsPage },
  { path: "/notifications/send", component: SendNotificationPage },

  // app configurations
  { path: "/app-configurations", component: AppConfigurations },
  
  // app customization
  { path: "/app-customization", component: AppCustomization },
  
  // referral
  { path: "/referral", component: Refral },
  
  // referred users
  { path: "/referred-users", component: ReferredUsers },
  
  // payments
  { path: "/payments", component: PaymentsPage },
  { path: "/payments/manual", component: PaymentsPage },
  { path: "/payments/api", component: PaymentsPage },
  // sales report
  { path: "/sales-report", component: SalesReport },
  { path: "/sales-report/data", component: SalesData },

  // role creation
  { path: "/role-creation", component: RoleCreation },

  // agent commission summary
  { path: "/agent-commission-summary/:agentId", component: AgentCommissionSummary },

  // game rates
  { path: "/game-rates", component: GameRates },

  // change password
  { path: "/change-password", component: ChangePassword },

  // general settings
  { path: "/general-settings", component: GeneralSettings },

  // HFCM
  { path: "/hfcm", component: HFCM },

  // Role-based paths for Agent (Internal)
  { path: "/agentinternal", component: Dashboard },
  { path: "/agentinternal/dashboard", component: Dashboard },
  { path: "/agentinternal/create-user", component: Users },
  { path: "/agentinternal/referral", component: Refral },
  { path: "/agentinternal/referred-users", component: ReferredUsers },

  // Role-based paths for Finance
  { path: "/finance", component: Dashboard },
  { path: "/finance/dashboard", component: Dashboard },
  { path: "/finance/starline/market", component: Market },
  //{ path: "/finance/withdraw-history", component: WithdrawHistory },
  { path: "/finance/users", component: Users },
  { path: "/finance/payments", component: PaymentsPage },
  { path: "/finance/payments/manual", component: PaymentsPage },
  { path: "/finance/payments/api", component: PaymentsPage },
  { path: "/finance/withdraw-request", component: WithdrawRequest },
  { path: "/finance/add-fund", component: AddFund },
  { path: "/finance/deposit-request", component: DepositRequest },
  { path: "/finance/deposit-analytics", component: DepositAnalytics },
  { path: "/finance/sales-report", component: SalesReport },
  { path: "/finance/sales-report/data", component: SalesData },
  { path: "/finance/bank-details", component: BankDetails },
  { path: "/finance/change-password", component: ChangePassword },
  { path: "/finance/general-settings", component: GeneralSettings },

  // Role-based paths for Support
  { path: "/support", component: Dashboard },
  { path: "/support/dashboard", component: Dashboard },
  { path: "/support/bet-history", component: BetHistory },
  { path: "/support/withdraw-history", component: WithdrawRequest },
  { path: "/support/deposit-history", component: DepositRequest },
  { path: "/support/referral", component: Refral },
  { path: "/support/referred-users", component: ReferredUsers },
  { path: "/support/change-password", component: ChangePassword },

  // Role-based paths for Sub Admin (same as Admin but without role-creation)
  { path: "/subadmin", component: Dashboard },
  { path: "/subadmin/dashboard", component: Dashboard },
  { path: "/subadmin/market", component: Market },
  { path: "/subadmin/declare-result", component: DeclareResult },
  { path: "/subadmin/starline/market", component: StarlineMarket },
  { path: "/subadmin/starline/bet-history", component: StarlineBetHistory },
  { path: "/subadmin/starline/declare-result", component: StarlineDeclareResult },
  { path: "/subadmin/bet-history", component: BetHistory },
  { path: "/subadmin/players", component: PlayersPage },
  { path: "/subadmin/player-app-status", component: PlayerAppStatusPage },
  { path: "/subadmin/players/:playerId", component: PlayerDetailsPage },
  { path: "/subadmin/payout-multiplier", component: PayoutMultiplier },
  { path: "/subadmin/withdraw-request", component: WithdrawRequest },
  { path: "/subadmin/deposit-request", component: DepositRequest },
  { path: "/subadmin/deposit-analytics", component: DepositAnalytics },
  { path: "/subadmin/add-fund", component: AddFund },
  { path: "/subadmin/bank-details", component: BankDetails },
  { path: "/subadmin/user-profile", component: UserProfile },
  { path: "/subadmin/users", component: Users },
  { path: "/subadmin/notifications", component: NotificationsPage },
  { path: "/subadmin/notifications/send", component: SendNotificationPage },
  { path: "/subadmin/referral", component: Refral },
  { path: "/subadmin/referred-users", component: ReferredUsers },
  { path: "/subadmin/payments", component: PaymentsPage },
  { path: "/subadmin/payments/manual", component: PaymentsPage },
  { path: "/subadmin/sales-report", component: SalesReport },
  { path: "/subadmin/sales-report/data", component: SalesData },
  { path: "/subadmin/hfcm", component: HFCM },
  { path: "/subadmin/app-configurations", component: AppConfigurations },
  { path: "/subadmin/general-settings", component: GeneralSettings },
  { path: "/subadmin/game-rates", component: GameRates },
  { path: "/subadmin/change-password", component: ChangePassword },
  { path: "/subadmin/agent-commission-summary/:agentId", component: AgentCommissionSummary },
  
];

const publicRoutes = [

  // authentication
  { path: "/login", component: Login },
  { path: "/logout", component: Logout },
  { path: "/register", component: Register },

]

export { authProtectedRoutes, publicRoutes };
